import React, {type Dispatch,
    type SetStateAction,
    useCallback,
} from 'react';


import {FormInput, HtmlArea} from 'src/components/input';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';

import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';

import {EmailSetupSkeleton} from './email-setup.skeleton';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {EmailCustomization} from 'src/openapi/admin/models/EmailCustomization';
import type {Error} from 'src/openapi/admin/models/Error';

type Props = {
    emailBodyKey: number,
    isLoading: boolean,
    loadingError: Error | null,
    data: EmailCustomization,
    setData: Dispatch<SetStateAction<EmailCustomization>>,
    trackChange: (parameter: string) => void,
    readonly?: boolean,
};

export const EmailSetupSection = (props: Props) => {
    const {
        emailBodyKey,
        isLoading,
        loadingError,
        data,
        setData,
        trackChange,
        readonly,
    } = props;

    const changeSubject = useCallback((subject: string) => {
        setData(prevState => ({...prevState, subject}));
        trackChange('Email Subject');
    }, [setData, trackChange]);

    const changeBody = useCallback((body: string) => {
        setData(prevState => ({...prevState, body}));
        trackChange('Email body');
    }, [setData, trackChange]);

    return (
        <Skeleton shown={isLoading} view={<EmailSetupSkeleton/>}>
            <ServerResponseHandler
                smallView={true}
                sectionName="Email Setup"
                accessRight={ACCESS_RIGHTS.MICROSITES_SCREEN_VIEW as AccessRight}
                serverError={loadingError}>
                <FormSection headline="Email Setup">
                    <Group direction="v" alignItems="start">
                        <FormInput
                            width={300}
                            placeholder="Site Name"
                            label="Email Subject"
                            readOnly={readonly}
                            maxLength={255}
                            value={data.subject}
                            onBlur={changeSubject}
                        />
                        <Item style={{width: 624}}>
                            <HtmlArea
                                disabled={readonly}
                                maxCharCount={2000}
                                key={emailBodyKey}
                                value={data.body}
                                onChange={changeBody}
                            />
                        </Item>
                    </Group>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
